import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, message, Popconfirm, Spin, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { DepartmentDto, LeaveApprovalHierarchy, HierarchyStepPayload } from '../../../types/department.types';
import { EligibleApprover } from '../../../types/user.types';
import departmentService from '../../../services/department/department.service';
import userService from '../../../services/user/user.service';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MenuOutlined } from '@ant-design/icons';

const { Option } = Select;

interface LeaveHierarchyModalProps {
  visible: boolean;
  onClose: () => void;
  onHierarchyUpdate: () => void;
  department: DepartmentDto | null;
}

const LeaveHierarchyModal: React.FC<LeaveHierarchyModalProps> = ({ visible, onClose, onHierarchyUpdate, department }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [eligibleApprovers, setEligibleApprovers] = useState<EligibleApprover[]>([]);
  const [steps, setSteps] = useState<HierarchyStepPayload[]>([]);
  const [initialHierarchy, setInitialHierarchy] = useState<LeaveApprovalHierarchy | null>(null);

  useEffect(() => {
    if (visible && department) {
      fetchData();
    } else {
      // Reset state when modal is closed
      setSteps([]);
      setInitialHierarchy(null);
    }
  }, [visible, department]);

  const fetchData = async () => {
    if (!department) return;
    setLoading(true);
    try {
      const [approvers, hierarchy] = await Promise.all([
        userService.getEligibleApprovers(),
        departmentService.getDepartmentHierarchy(department.id)
      ]);
      setEligibleApprovers(approvers);
      if (hierarchy && hierarchy.steps) {
        const sortedSteps = hierarchy.steps.sort((a, b) => a.stepOrder - b.stepOrder);
        setSteps(sortedSteps.map(step => ({ approverId: step.approver.id, stepOrder: step.stepOrder })));
        setInitialHierarchy(hierarchy);
      } else {
        setSteps([]);
        setInitialHierarchy(null);
      }
    } catch (error) {
      message.error(t('common.error.defaultErrorMessage', 'Bir hata oluştu.'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = () => {
    if (steps.length >= 5) {
      message.warning(t('departments.hierarchy.maxStepsWarning', 'En fazla 5 onaycı ekleyebilirsiniz.'));
      return;
    }
    setSteps([...steps, { approverId: 0, stepOrder: steps.length + 1 }]);
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    // Re-order steps
    const reorderedSteps = newSteps.map((step, i) => ({ ...step, stepOrder: i + 1 }));
    setSteps(reorderedSteps);
  };

  const handleApproverChange = (approverId: number, index: number) => {
    const newSteps = [...steps];
    newSteps[index].approverId = approverId;
    setSteps(newSteps);
  };

  const handleSave = async () => {
    if (!department) return;

    // Check for duplicate approvers
    const approverIds = steps.map(step => step.approverId);
    if (new Set(approverIds).size !== approverIds.length) {
      message.error(t('departments.hierarchy.duplicateApproverError', 'Aynı onaycıyı birden fazla ekleyemezsiniz.'));
      return;
    }
    
    // Check if all steps have an approver selected
    if (steps.some(step => !step.approverId)) {
        message.error(t('departments.hierarchy.approverRequiredError', 'Lütfen her adım için bir onaycı seçin.'));
        return;
    }

    setLoading(true);
    try {
      await departmentService.createOrUpdateDepartmentHierarchy(department.id, steps);
      message.success(t('departments.hierarchy.saveSuccess', 'Hiyerarşi başarıyla kaydedildi.'));
      onHierarchyUpdate();
      onClose();
    } catch (error) {
      message.error(t('common.error.defaultErrorMessage', 'Bir hata oluştu.'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!department || !initialHierarchy) return;
    setLoading(true);
    try {
      await departmentService.deleteDepartmentHierarchy(department.id);
      message.success(t('departments.hierarchy.deleteSuccess', 'Hiyerarşi başarıyla silindi.'));
      onHierarchyUpdate();
      onClose();
    } catch (error) {
        message.error(t('common.error.defaultErrorMessage', 'Bir hata oluştu.'));
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
  
    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
  
    const updatedSteps = items.map((item, index) => ({
      ...item,
      stepOrder: index + 1
    }));
  
    setSteps(updatedSteps);
  };

  const approverInUse = (approverId: number) => {
    return steps.some(step => step.approverId === approverId);
  }

  return (
    <Modal
      title={t('departments.hierarchy.modalTitle', { departmentName: department?.name })}
      visible={visible}
      onCancel={onClose}
      footer={[
        initialHierarchy && (
          <Popconfirm
            key="delete"
            title={t('departments.hierarchy.deleteConfirm', 'Bu hiyerarşiyi silmek istediğinizden emin misiniz?')}
            onConfirm={handleDelete}
            okText={t('common.buttons.yes', 'Evet')}
            cancelText={t('common.buttons.no', 'Hayır')}
          >
            <Button danger loading={loading}>
              {t('common.buttons.delete', 'Sil')}
            </Button>
          </Popconfirm>
        ),
        <Button key="back" onClick={onClose}>
          {t('common.buttons.cancel', 'İptal')}
        </Button>,
        <Button key="submit" loading={loading} onClick={handleSave} className="bg-dinox-purple text-white">
          {t('common.buttons.save', 'Kaydet')}
        </Button>,
      ]}
      width={700}
    >
      <Spin spinning={loading}>
      <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="steps">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {steps.map((step, index) => (
                  <Draggable key={index} draggableId={`step-${index}`} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          userSelect: 'none',
                          padding: '10px',
                          margin: '0 0 8px 0',
                          backgroundColor: '#f9f9f9',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Row align="middle" gutter={16}>
                          <Col {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                            <MenuOutlined />
                          </Col>
                          <Col>
                            <span style={{ fontWeight: 'bold' }}>{index + 1}. {t('departments.hierarchy.approver', 'Onaycı')}:</span>
                          </Col>
                          <Col flex="auto">
                            <Select
                              showSearch
                              style={{ width: '100%' }}
                              placeholder={t('departments.hierarchy.selectApprover', 'Onaycı Seçin')}
                              value={step.approverId || null}
                              onChange={(value) => handleApproverChange(value, index)}
                              filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {eligibleApprovers.map(approver => (
                                <Option key={approver.id} value={approver.id} disabled={approverInUse(approver.id) && step.approverId !== approver.id}>
                                  {`${approver.firstName} ${approver.lastName} (${approver.role})`}
                                </Option>
                              ))}
                            </Select>
                          </Col>
                          <Col>
                            <Button type="primary" danger onClick={() => handleRemoveStep(index)}>
                              {t('common.buttons.delete', 'Sil')}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        
        <Button type="dashed" onClick={handleAddStep} style={{ width: '100%', marginTop: '10px' }}>
          + {t('departments.hierarchy.addStep', 'Onaycı Ekle')}
        </Button>
      </Spin>
    </Modal>
  );
};

export default LeaveHierarchyModal;
